import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createFlag } from "../api/endpoints";
import { canWrite, useAuth } from "../auth/AuthContext";
import { EmptyState } from "../components/EmptyState";
import { useToast } from "../components/Toast";
import { useWorkspaceEnv } from "../state/WorkspaceEnvContext";

export function FlagNewPage() {
  const { workspace } = useWorkspaceEnv();
  const { identity } = useAuth();
  const { pushError, push } = useToast();
  const navigate = useNavigate();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [owner, setOwner] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  const keyPattern = /^[a-z0-9][a-z0-9_-]{0,63}$/;

  if (!workspace) return null;
  // A viewer may read everything and change nothing (CONTRACT §2.2).
  if (!canWrite(identity?.role)) {
    return (
      <EmptyState
        title="Editor role required"
        message="Your account may read flags but not create them."
      />
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!keyPattern.test(key)) {
      setKeyError("must match ^[a-z0-9][a-z0-9_-]{0,63}$ (lowercase, digits, - or _)");
      return;
    }
    setSubmitting(true);
    try {
      const flag = await createFlag(workspace!.id, {
        key,
        name,
        description: description || undefined,
        tags: tags
          ? tags
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean)
          : undefined,
        owner: owner || undefined,
      });
      push({ kind: "success", title: `Flag "${flag.key}" created` });
      navigate(`/flags/${flag.key}`);
    } catch (err) {
      pushError(err, "Could not create flag");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 480 }}>
      <h1>New flag</h1>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="flag-key">Key</label>
          <input
            id="flag-key"
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              setKeyError(null);
            }}
            required
          />
          {keyError && <span className="error">{keyError}</span>}
        </div>
        <div className="field">
          <label htmlFor="flag-name">Name</label>
          <input id="flag-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="flag-description">Description</label>
          <textarea
            id="flag-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
        <div className="field">
          <label htmlFor="flag-tags">Tags (comma-separated)</label>
          <input id="flag-tags" value={tags} onChange={(e) => setTags(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="flag-owner">Owner</label>
          <input id="flag-owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
        </div>
        <button className="btn primary" type="submit" disabled={submitting || !key || !name}>
          {submitting ? "Creating…" : "Create flag"}
        </button>
      </form>
    </div>
  );
}
